import { makeAutoObservable } from 'mobx';
import { Axios } from '../utilities/network';
import { isWeb } from '../utilities/platform';
import RootStore from './root-store';
import { navigate } from 'gatsby';
import { AccountDetails, AttachmentType, LoginInfo, MFAType, StatusCodes, BasicInfo, VerificationCodeType, VmList, ContactCategory, ChangeSectionCategory, MembershipStatus, Language, ExportTemplate, ExportFileFormat, CreateInsuranceProviderProps, UpdateInsuranceProviderProps, ImportMemberResponse, ComplianceDocumentAttachmentUpdate, ComplianceDocumentAttachmentCreate, PlatformType, SimplifiedAccountDetails, InsuranceDocumentAttachment, InternalNote, ImportMedibankFileError, ImportMedibankFileResponse, GENERAL_DOCUMENT_LOCAL_CATEGORY_ID, NotificationContactSearchResponse, NotificationFilterExpiryType, VmListT, ReportExportLog, ComplianceDocumentCategory, ChangeLogReturnObject } from '../constants/options';
import { ClearLocalStorage, SetLocalStorage } from '../utilities/cryptography';
import { EXPORTABLE_DATA, EXPORTABLE_DATA_EXPIRY, USER_INFO } from '../constants/storage-keys';
import { Qualification, QualificationAttachment } from '../components/profile-page/qualifications/qualifications';
import { NewAttachment } from '../utilities/file';
import { isDevelopment } from '../constants/settings';
import axios from 'axios';
import { GetUserProfile, HashString } from '../utilities/general';
import { ClinicInfo } from '../components/profile-page/clinic-details/single-clinic-info';

export interface searchNotificationContactsBody {
  ids?: number[],
  cMemberId?: number,
  contactCategoryIds?: number[],
  isQualified?: boolean,
  name?: string,
  firstName?: string,
  lastName?: string,
  chineseName?: string,
  emailAddress?: string,
  phone?: string,
  mobile?: string,
  fax?: string,
  modalityIds?: number[],
  contactTitleIds?: number[],
  referee?: string,
  languagePreference?: string,
  state?: string[],
  membershipJoinDateFrom?: string,
  membershipJoinDateTo?: string,
  membershipStartDateFrom?: string,
  membershipStartDateTo?: string,
  membershipExpiryDateFrom?: string,
  membershipExpiryDateTo?: string,
  cpdPackageStartDateFrom?: string,
  cpdPackageStartDateTo?: string,
  cpdPackageExpriedDateFrom?: string,
  cpdPackageExpriedDateTo?: string,
  cpdLodged?: string,
  cpdIds?: number[],
  totalCreditsFrom?: number,
  totalCreditsTo?: number,
  createdBy?: number,
  createdAtFrom?: string,
  createdAtTo?: string,
  lastModifiedBy?: number,
  lastModifiedAtFrom?: string,
  lastModifiedAtTo?: string,
  membershipLevelId?: number,
  membershipStatusIds?: MembershipStatus[],

  hasEmail?: boolean,
  hasAPP?: boolean,
  hasSMS?: boolean,

  filterCompDocsExpiryCategoryId?: ComplianceDocumentCategory
  filterCompDocsExpiryDateFrom?: string;
  filterCompDocsExpiryDateTo?: string;
  fliterExpiryType?: NotificationFilterExpiryType,
}

export default class UserStore {
  get isAuthed() {
    return isWeb && localStorage.USER_TOKEN != null && localStorage.USER_INFO != null;
  }

  set isAuthed(value) {
    console.log('Deleting token...');
    if (!value && isWeb) {
      ClearLocalStorage();
      navigate('/');
    }
  }

  signingOut: boolean = false;

  loading: boolean = false;
  loadingInternalNotes: boolean = false;

  loadingZeroCpdMembers: boolean = false;

  loadingCpdTotalCredit: boolean = false;

  loadingChangeHistory: boolean = false;
  generatingClinicPDF: boolean = false;
  loadingAccountDetails: boolean = false;
  savingAccountDetails: boolean = false;

  loadingNotificationContacts: boolean = false;

  isClosingMedicalInsuranceProvider: boolean = false;

  deactivatingMembership: boolean = false;

  loadingInsuranceDocument: boolean = false;
  downloadingInsuranceDocument: boolean = false;
  savingInsuranceDocument: boolean = false;

  deletingMember: boolean = false;

  savingInternalNote: boolean = false;
  deletingInternalNote: boolean = false;

  uploadingInsuranceList: boolean = false;
  searchingInsuranceList: boolean = false;

  loadingBasicInfo: boolean = false;
  savingBasicInfo: boolean = false;

  loadingServicesProvided: boolean = false;
  savingServicesProvided: boolean = false;

  loadingClinic: boolean = false;
  loadingClinicInfo: boolean = false;
  loadingQualifications: boolean = false;
  loadingQualificationAttachments: boolean = false;

  generatingClinicAddress: boolean = false;

  approvingMemberSection: boolean = false;

  savingPhotoIdDocuments: boolean = false;
  loadingPhotoIdDocuments: boolean = false;

  checkingPendingApproval: boolean = false;

  loadingComplianceDocuments: boolean = false;
  savingComplianceDocuments: boolean = false;

  loadingExportableData: boolean = false;
  loadingExportTemplate: boolean = false;
  savingExportTemplate: boolean = false;

  downloading: boolean = false;
  downloadingApplicationForm: boolean = false;
  downloadingEntireTable: boolean = false;
  creatingNewAccount: boolean = false;

  checkingUsernameAvailability: boolean = false;

  deletingContact: boolean = false;

  signingIn: boolean = false;
  sending: boolean = false;
  staffList: VmList = { totalCount: 0, items: [] };
  editToken: string | null = null;

  profilePageTab: number = 0;

  userToken: string | null = null;
  rootStore: RootStore;

  assigningAdminRole: boolean = false;

  sendingMessage: boolean = false;

  userProfileCache = new Map<number, AccountDetails>();

  adminProfileCache = new Map<number, AccountDetails>();

  notificationContactInfoCache = new Map<number, SimplifiedAccountDetails>();

  memberClinicCache = new Map<number, ClinicInfo[]>();

  searchNotificationContactsCache = new Map<string, any>();

  internalNotesCache = new Map<string, any>();

  searchStaffLoginLogCache = new Map<string, any>();

  sendingResetPasswordEmail: boolean = false;

  signatureLoading: boolean = false;

  refreshMemberDetailsFn: (() => void) | null = null;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.rootStore = rootStore;
  }

  signIn = async (userModel: {
    username: string,
    password: string,
    code?: string,
    sid?: string,
    categoryId?: VerificationCodeType | null | undefined;
  }) => {
    this.signingIn = true;
    try {
      const res: { data: LoginInfo; } = await Axios.post(`/api/Auth/Admin/SignIn?platformId=${PlatformType.ADMIN_WEB}`, userModel);

      if (res.data.token !== null) {
        const token = `Bearer ${res.data.token}`;
        this.userToken = token;

        localStorage.USER_TOKEN = token;
        SetLocalStorage(USER_INFO, res.data);
      }

      return Promise.resolve(res.data);
    } catch (err) {
      this.isAuthed = false;
      return Promise.reject(err);
    } finally {
      this.signingIn = false;
    }
  };

  signOut = async (isLocal?: boolean) => {
    const localSignOut = () => {
      this.isAuthed = false;
      this.userToken = null;
      navigate('/');
      ClearLocalStorage();
      this.rootStore.notify('Bye', "success");
    };

    const userInfo = GetUserProfile();
    if (userInfo && !isLocal) {
      this.signingOut = true;
      try {
        const contactId = userInfo.contactId;

        const res = await Axios.post(`/api/Auth/Admin/SignOut`, { contactId });
        return Promise.resolve(res.data);
      } catch (err) {
        return Promise.reject(err);
      } finally {
        localSignOut();
        this.signingOut = false;
      }
    } else {
      localSignOut();
    }
  };

  signUp = async (userModel: {
    username: string,
    password: string,
    title: string,
    firstName: string,
    lastName: string,
    chineseLastName?: string,
    chineseFirstName?: string,
    dateOfBirth?: string,
    emailAddress: string,
    phone?: string,
    mobile: string,
    // fax?: string,
    referee: string,
    languagePreference: string,
    location?: string,
    userBio?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Auth/Member/SignUp?platformId=${PlatformType.ADMIN_WEB}`, userModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createAccount = async (userModel: {
    username: string | null,
    password: string | null,
    firstName: string | null,
    lastName: string | null,
    chineseLastName?: string | null,
    chineseFirstName?: string | null,
    dateOfBirth?: string | null,
    emailAddress?: string | null,
    mobile: string | null,
    fax?: string | null,
    title?: string | null,
    gender?: string | null,
    referee?: string | null,
    languagePreference?: string | null,
    location?: string | null,
    userBio?: string | null,
    contactCategoryId: ContactCategory,
    roleId?: number,
    cMemberId?: number;
  }) => {
    this.creatingNewAccount = true;
    try {
      const res = await Axios.post(`/api/Auth/Admin/SignUp`, userModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creatingNewAccount = false;
    }
  };

  updateMemberId = async (reqModal: {
    contactId: number,
    memberId: number;
  }) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/Contact/MemberId`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  // CONTACT INFO CHANGE IS NOT TRACKED
  getAccountDetails = async (userId: number, forceRefresh = false) => {
    if (!userId) return Promise.reject(new Error("User ID is required"));

    if (this.userProfileCache.has(userId) && !forceRefresh) {
      return Promise.resolve(this.userProfileCache.get(userId));
    }

    this.loadingAccountDetails = true;
    try {
      const res: { data: AccountDetails; } = await Axios.get(`/api/Contact/${userId}`);
      this.userProfileCache.set(userId, res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingAccountDetails = false;
    }
  };

  // Cached lookup of an admin/staff profile, used to resolve createdBy / lastModifiedBy ids into names
  adminProfilePromiseCache = new Map<number, Promise<AccountDetails>>();

  getAdminProfile = async (contactId: number, forceRefresh = false): Promise<AccountDetails> => {
    if (!contactId) return Promise.reject(new Error("Contact ID is required"));

    if (!forceRefresh) {
      const cached = this.adminProfileCache.get(contactId);
      if (cached) return Promise.resolve(cached);

      const pending = this.adminProfilePromiseCache.get(contactId);
      if (pending) return pending;
    }

    const request = Axios.get(`/api/Contact/${contactId}`)
      .then((res: { data: AccountDetails; }) => {
        this.adminProfileCache.set(contactId, res.data);
        return res.data;
      })
      .finally(() => this.adminProfilePromiseCache.delete(contactId));

    this.adminProfilePromiseCache.set(contactId, request);
    return request;
  };

  updateAccountDetails = async (reqModel: {
    id: number,
    title: string | null,
    gender: string | null,
    firstName: string | null,
    lastName: string | null,
    chineseLastName: string | null,
    chineseFirstName: string | null,
    dateOfBirth: string | null,
    membershipLevelId: number,
    languagePreference?: Language | null | undefined,
    membershipJoinDate: string,
    membershipStartDate: string,
    membershipExpiryDate: string,
    contactTitleId: number;
  } | any) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/Contact/Info`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  updateAccountContactDetails = async (accountDetails: AccountDetails | any) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/Contact/`, accountDetails);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  // ================ START 2FA ================

  sendLoginMFACode = async (reqModal: {
    username: string,
    password: string,
    categoryId?: VerificationCodeType | null | undefined;
  }) => {
    this.sending = true;
    try {
      const res = await Axios.post("/api/Auth/Verification/Code/Send/Signin", reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.sending = false;
    }
  };

  sendVerificationCode = async (isEdit: boolean, reqModal: {
    email?: string,
    phone?: string,
    categoryId: VerificationCodeType;
  }) => {
    this.sending = true;
    try {
      const res = await Axios.post(isEdit ? `/api/Auth/Verification/Code/Send/Edit/${reqModal.categoryId}` : "/api/Auth/Verification/Code/Send", reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.sending = false;
    }
  };

  updateContactDetails = async (contactInfo: any) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/Contact/`, contactInfo);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  verifyCode = async (isEdit: boolean, reqModal: {
    code: string,
    sid: string | null,
    categoryId: VerificationCodeType;
  }) => {
    this.loading = true;
    try {
      const res: { data: string; } = await Axios.post(isEdit ? `/api/Auth/Verification/Code/Verify/Edit` : `/api/Auth/Verification/Code/Verify`, reqModal);
      if (isEdit) this.editToken = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  setUpMFAQRCode = async () => {
    this.loading = true;
    try {
      const res = await Axios.get("/api/Auth/totp");
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  checkMFACode = async (recipient: string, username: string, mfaType: MFAType, verificationCode: string, verificationSid?: string) => {
    this.loading = true;
    try {
      let url = ``;
      if (mfaType === MFAType.SMS) url = `/api/Auth/Verify/Sms/Check?sid=${verificationSid}&verCode=${verificationCode}`;
      else if (mfaType === MFAType.EMAIL) url = `/api/Auth/Verify/Email/Check?sid=${verificationSid}&verCode=${verificationCode}`;
      else Promise.reject(new Error("Invalid MFA Type"));

      this.rootStore.notify(`Pending implementation: checking verification code ${url}`, 'warning');

      const res = {
        data: {
          success: true
        }
      };
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  updateContactContact = async (
    contactInfo: {
      id: number,
      emailAddress: string,
      phone: string,
      mobile: string,
      fax: string,
      referee: string,
      languagePreference: string,
      location: string,
      userBio: string,
      twofaSms: string | null,
      twofaEmail: string | null,
      hasTwofaApp: boolean,
      hasTwofaSms: boolean,
      hasTwofaEmail: boolean,
      cpdLodged: string;
    } | any) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Contact?token=${this.editToken}`, contactInfo);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  // ================ END 2FA ================

  updateAdmin = async (reqModel: {
    contactId: number,
    roleId: number,
    firstName: string,
    lastName: string,
    email: string,
    mobile: string,
    id: number,
    contactCategoryId: number;
  }) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/AdminRole/Contact`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  deleteMember = async (id: number) => {
    this.deletingMember = true;
    try {
      const res = await Axios.delete(`/api/Contact/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingMember = false;
    }
  };

  getBasicInfo = async (userId: number) => {
    this.loadingBasicInfo = true;
    try {
      const res: { data: BasicInfo[]; } = await Axios.post(`/api/Member/Basic/Search/${userId}`);

      // @ts-ignore
      let response: BasicInfo = { id: -1 };
      if (res.data.length > 0) response = res.data[0];

      return Promise.resolve(response);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingBasicInfo = false;
    }
  };

  getBasicInfoById = async (id: number) => {
    this.loadingBasicInfo = true;
    try {
      const res: { data: BasicInfo[]; } = await Axios.get(`/api/Member/Basic/${id}`);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingBasicInfo = false;
    }
  };

  updateBasicInfo = async (userInfo: {
    id: number,
    contactId: number,
    homeAddress: string | null,
    homeSuburb: string | null,
    homeState: string | null,
    homePostcode: string | null,
    postalAddress: string | null,
    postalSuburb: string | null,
    postalState: string | null,
    postalPostcode: string | null,
    note: string,
    // previousId: number,
    // originalId: number,
    // isDeleted: true,
    approvalStatusId: StatusCodes,
  }) => {
    this.savingBasicInfo = true;
    try {
      const res = await Axios.put(`/api/Member/Basic`, userInfo);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingBasicInfo = false;
    }
  };

  createBasicInfo = async (userInfo: {
    contactId: number,
    homeAddress: string | null,
    homeSuburb: string | null,
    homeState: string | null,
    homePostcode: string | null,
    postalAddress: string | null,
    postalSuburb: string | null,
    postalState: string | null,
    postalPostcode: string | null,
    note: string,
    // previousId: number,
    // originalId: number,
    // isDeleted: boolean,
    approvalStatusId: number;
  }) => {
    this.savingBasicInfo = true;
    try {
      const res = await Axios.post(`/api/Member/Basic`, userInfo);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingBasicInfo = false;
    }
  };

  deleteContact = async (id: number) => {
    this.deletingContact = true;
    try {
      const res = await Axios.delete(`/api/Contact/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingContact = false;
    }
  };

  // ============ Member Service ============

  getMemberServicesList = async (memberId: number, abortSignal?: AbortSignal) => {
    this.loadingServicesProvided = true;
    try {
      const res = await Axios.get(`/api/Member/Service/Search/${memberId}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingServicesProvided = false;
    }
  };

  getMemberService = async (id: number) => {
    this.loadingServicesProvided = true;
    try {
      const res = await Axios.get(`/api/Member/Service/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingServicesProvided = false;
    }
  };

  createMemberService = async (req: {
    memberId: number,
    servicesCategoryId: number,
    originalId?: number,
    previousId?: number,
    isDeleted?: boolean,
    approvalStatusId: StatusCodes,
    startDate?: string,
    endDate?: string,
  }) => {
    this.savingServicesProvided = true;
    try {
      const res = await Axios.post(`/api/Member/Service/`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingServicesProvided = false;
    }
  };

  updateMemberService = async (req: {
    memberId: number,
    servicesCategoryId: StatusCodes,
    originalId?: number,
    previousId?: number,
    isDeleted?: boolean,
    approvalStatusId: StatusCodes,
    startDate?: string,
    endDate?: string,
    id?: number,
    note?: string,
  }) => {
    this.savingServicesProvided = true;
    try {
      const res = await Axios.put(`/api/Member/Service/`, req);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingServicesProvided = false;
    }
  };

  deleteMemberService = async (serviceId: number) => {
    this.savingServicesProvided = true;
    try {
      const res = await Axios.delete(`/api/Member/Service/${serviceId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingServicesProvided = false;
    }
  };

  // ============ Clinic ============

  searchMemberClinic = async (memberId: number, config: { triggerLoad?: boolean, forceRefresh?: boolean, } = { triggerLoad: true, forceRefresh: true }) => {
    const { triggerLoad, forceRefresh } = config;

    if (triggerLoad) { this.loadingClinic = true; }
    try {
      if (this.memberClinicCache.has(memberId) && !forceRefresh) {
        return Promise.resolve(this.memberClinicCache.get(memberId));
      }
      const res = await Axios.get(`/api/Member/Clinic/Search/${memberId}`);
      this.memberClinicCache.set(memberId, res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingClinic = false;
    }
  };

  getClinic = async (id: number) => {
    this.loadingClinicInfo = true;
    try {
      const res = await Axios.get(`/api/Member/Clinic/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingClinicInfo = false;
    }
  };

  createClinic = async (clinicInfo: any) => {
    this.loadingClinicInfo = true;
    try {
      const res = await Axios.post(`/api/Member/Clinic`, clinicInfo);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingClinicInfo = false;
    }
  };

  updateClinic = async (clinicInfo: any) => {
    this.loadingClinicInfo = true;
    try {
      const res = await Axios.put(`/api/Member/Clinic??token=1`, clinicInfo);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingClinicInfo = false;
    }
  };

  deleteClinic = async (clinicId: number) => {
    this.loadingClinicInfo = true;
    try {
      const res = await Axios.delete(`/api/Member/Clinic/${clinicId}?token=1`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingClinicInfo = false;
    }
  };

  // ============ CLINIC ADDRESS LINE API ============
  getAHMBUPAClinicAddressLine = async (reqModal: { shopNo: string, shopCenterName: string, address: string, }, signal?: AbortSignal) => {
    this.generatingClinicAddress = true;
    try {
      const res = await Axios.post(`/api/Member/Clinic/Address/Format`, reqModal, { signal: signal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingClinicAddress = false;
    }
  };


  // ============ Medical Insurance Provider ============

  searchMedicalInsuranceProvider = async (skip: number, take: number, reqModel: {
    memberId: number,
    clinicId?: number,
    providerCategoryId?: number,
    acu?: boolean,
    rem?: boolean,
    chm?: boolean,
    acuNo?: string,
    remNo?: string,
    chmNo?: string,
    isClosed?: boolean,
    isActive?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  getMedicalInsuranceProvider = async (id: number, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Insurance/${id}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createMedicalInsuranceProvider = async (req: CreateInsuranceProviderProps) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateMedicalInsuranceProvider = async (userInfo: UpdateInsuranceProviderProps, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Member/Insurance/`, userInfo, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  deleteMedicalInsuranceProvider = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Member/Insurance/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // -------------------- CLOSE INSURANCE PROVIDER --------------------


  closeReopenMedicalInsuranceProvider = async (reqModal: {
    ids: number[],
    memberId: number,
    clinicId: number,
    isClosed: boolean;
  }) => {
    this.isClosingMedicalInsuranceProvider = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Close`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.isClosingMedicalInsuranceProvider = false;
    }
  };


  // ============ Other Membership ============

  searchMembershipAssociationOfMember = async (memberId: number, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Association/Membership/Search/${memberId}`, {}, { signal: abortSignal });

      // const hasCmba = res.data.filter((item: any) => item.associationName === "CMBA" && !item.isDeleted).length > 0;
      // this.memberEnteredCmba = hasCmba;

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getOtherAssociation = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Association/Membership/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createOtherAssociation = async (reqModel: any | {
    memberId: number,
    approvalStatusId: number,
    associationName: string,
    membershipNumber: number,
    previousId: number | null,
    originalId: number | null,
    isDeleted: true,
    associationMembershipId: number;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Association/Membership`, reqModel);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateOtherAssociation = async (reqModel: {
    memberId: number,
    approvalStatusId: number,
    associationName: "string",
    membershipNumber: number,
    previousId: number | null,
    originalId: number | null,
    isDeleted: boolean,
    id: number;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Member/Association/Membership`, reqModel);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  deleteOtherAssociation = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.delete(`/api/Member/Association/Membership/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ============ Business Practice ============

  searchMemberBusinessPractice = async (memberId: number, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Practice/Search/${memberId}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getMemberBusinessPractice = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Practice/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createMemberBusinessPractice = async (reqModel: {
    memberId: number,
    clinicTradingName: string,
    abn: number | null,
    previousId: number | null,
    originalId: number | null,
    approvalStatusId: number,
    isDeleted: boolean,
    note?: string,
  } | any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Practice?token=${this.editToken}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateMemberBusinessPractice = async (reqModel: {
    id: number,
    memberId: number,
    clinicTradingName: string | null,
    abn: number | null,
    previousId: number,
    originalId: number,
    approvalStatusId: number,
    isDeleted: boolean,
    note?: string,
  } | any) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Member/Practice?token=${this.editToken}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };


  // ============ Qualifications ============

  getMemberQualifications = async (memberId: number, abortSignal?: AbortSignal) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.get(`/api/Member/Qualification/Search/${memberId}`, { signal: abortSignal });
      // this.memberQualifications = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  getQualifications = async (id: number) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.get(`/api/Member/Qualification/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  createQualification = async (qualification: Qualification & {
    uploadAttachments?: NewAttachment[],
  }) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.post(`/api/Member/Qualification`, qualification);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  updateQualification = async (qualification: Qualification & {
    uploadAttachments?: NewAttachment[],
    memberQualificationAttachments?: QualificationAttachment[],
  }) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.put(`/api/Member/Qualification`, qualification);

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  deleteQualification = async (id: number) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.delete(`/api/Member/Qualification/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  searchQualificationAttachment = async (reqModel: {
    qualificationId: number,
    searchTerm?: string;
  }) => {
    this.loadingQualificationAttachments = true;
    try {
      const res = await Axios.post(`/api/Member/Qualification/Attachment/Search/0/0`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.QUALIFICATION,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualificationAttachments = false;
    }
  };

  createQualificationAttachment = async (reqModel: {
    fileName: string,
    attachmentCategoryId: number,
    file: string,
    qualificationId: number,
    attachmentTypeId: number;
  }) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.post(`/api/Member/Qualification/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.QUALIFICATION,
      });

      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  updateQualificationAttachment = async (reqModel: {
    qualificationId: number,
    attachmentCategoryId: number,
    originalName: string,
    id: number,
    attachmentTypeId: number;
  }) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.put(`/api/Member/Qualification/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  deleteQualificationAttachment = async (id: number) => {
    this.loadingQualifications = true;
    try {
      const res = await Axios.delete(`/api/Member/Qualification/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingQualifications = false;
    }
  };

  downloadQualificationAttachment = async (savedName: string) => {
    this.downloading = true;
    try {
      const res = await Axios.get(`/api/Member/Qualification/Attachment/${savedName}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };



  // ============ Compliance Document ============

  searchComplianceDocumentList = async (skip: number, take: number, reqModel: {
    memberId?: number,
    documentCategoryId?: AttachmentType,
    providerName?: string,
    approvalStatusId?: StatusCodes,
    isActived?: boolean,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Compliance/Document/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchComplianceDocumentOfMember = async (memberId: number) => {
    this.loadingComplianceDocuments = true;
    try {
      const res = await Axios.get(`/api/Member/Compliance/Document/Search/${memberId}`);
      // this.memberComplianceDocuments = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingComplianceDocuments = false;
    }
  };

  getComplianceDocumentExpiryInfo = async (memberId: number, abortSignal?: AbortSignal) => {
    this.loadingComplianceDocuments = true;
    try {
      const res = await Axios.get(`/api/Member/Compliance/Document/Expiry/${memberId}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingComplianceDocuments = false;
    }
  };

  getComplianceDocument = async (id: number) => {
    this.loadingComplianceDocuments = true;
    try {
      const res = await Axios.get(`/api/Member/Compliance/Document/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingComplianceDocuments = false;
    }
  };

  createComplianceDocument = async (reqModel: {
    memberId: number,
    documentCategoryId: number,
    providerName: string,
    providerNumber: string,
    startAt: string,
    expiredAt: string,
    coverAmount: number,
    note: string,
    approvalStatusId: StatusCodes,
    isDeleted: boolean,
    originalId: number,
    previousId: number,
    uploadAttachments: ComplianceDocumentAttachmentCreate[],
    memberComplianceDocumentAttachments: ComplianceDocumentAttachmentUpdate[];
  }) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/Compliance/Document`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  updateComplianceDocument = async (reqModel: {
    memberId: number,
    documentCategoryId: number,
    providerName: string,
    providerNumber: string,
    startAt: string,
    expiredAt: string,
    coverAmount: number,
    note: string,
    approvalStatusId: StatusCodes,
    isDeleted: boolean,
    originalId: number,
    previousId: number,
    id: number,
    uploadAttachments: ComplianceDocumentAttachmentCreate[],
    memberComplianceDocumentAttachments: ComplianceDocumentAttachmentUpdate[];
  }) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.put(`/api/Member/Compliance/Document`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  deleteComplianceDocument = async (id: number) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.delete(`/api/Member/Compliance/Document/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  // ============ Compliance Document Attachment ============

  searchComplianceDocumentAttachmentList = async (reqModel: {
    documentId: number,
    attachmentCategoryId?: number,
    searchTerm?: string;
  }) => {
    this.loadingComplianceDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/Compliance/Document/Attachment/Search/0/0/`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingComplianceDocuments = false;
    }
  };

  createComplianceDocumentAttachment = async (reqModel: {
    fileName: string,
    file: string,
    documentId: number;
  }) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/Compliance/Document/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.COMPLIANCE_DOCUMENT
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  updateComplianceDocumentAttachment = async (reqModel: {
    documentId: number,
    originalName: string,
    id: number;
  }) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.put(`/api/Member/Compliance/Document/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.COMPLIANCE_DOCUMENT
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  deleteComplianceDocumentAttachment = async (id: number) => {
    this.savingComplianceDocuments = true;
    try {
      const res = await Axios.delete(`/api/Member/Compliance/Document/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingComplianceDocuments = false;
    }
  };

  downloadComplianceDocumentAttachment = async (savedName: string) => {
    this.downloading = true;
    try {
      const res = await Axios.get(`/api/Member/Compliance/Document/Attachment/${savedName}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };


  // DECLARATIONS 

  getDeclarationOfMember = async (memberId: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Declaration/Search/${memberId}`);
      // this.memberDeclarations = res.data;
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createDeclaration = async (declaration: {
    memberId: number,
    approvalStatusId: number,
    criminalOffense: boolean | null,
    criminalOffenseSpecify: string,
    underInvestigation: boolean | null,
    underInvestigationSpecify: string,
    suspended: boolean | null,
    suspendedSpecify: string,
    permitToWork: boolean | null,
    permitToWorkSpecify: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Declaration`, declaration);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  updateDeclaration = async (declaration: {
    memberId: number,
    approvalStatusId: number,
    criminalOffense: boolean | null,
    criminalOffenseSpecify: string,
    underInvestigation: boolean | null,
    underInvestigationSpecify: string,
    suspended: boolean | null,
    suspendedSpecify: string,
    permitToWork: boolean | null,
    permitToWorkSpecify: string,
    id: number;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/Member/Declaration`, declaration);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  checkIfPendingApproval = async (memberId: number) => {
    this.checkingPendingApproval = true;
    try {
      const res = await Axios.get(`/api/Member/Check/Approved/${memberId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.checkingPendingApproval = false;
    }
  };

  searchMemberList = async (skip: number, take: number, reqModal: {
    id?: number,
    cMemberId?: number,
    contactCategoryIds?: ContactCategory[],
    isQualified?: true,
    name?: string,
    firstName?: string,
    lastName?: string,
    chineseName?: string,
    emailAddress?: string,
    phone?: string,
    mobile?: string,
    state?: string,
    fax?: string,
    contactTitleId?: number,
    referee?: string,
    languagePreference?: string,
    location?: string,
    userBio?: string,
    membershipJoinDateFrom?: string,
    membershipJoinDateTo?: string,
    membershipStartDateFrom?: string,
    membershipStartDateTo?: string,
    membershipExpiryDateFrom?: string,
    membershipExpiryDateTo?: string,
    cpdPackageStartDateFrom?: string,
    cpdPackageStartDateTo?: string,
    cpdPackageExpriedDateFrom?: string,
    cpdPackageExpriedDateTo?: string,
    cpdLodged?: string,
    totalCreditsFrom?: number,
    totalCreditsTo?: number,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    membershipLevelId?: number,
    membershipStatusId?: number;
    membershipStatusIds?: number[],
  }, abortSignal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Contact/Search/${skip}/${take}`, {
        contactCategoryIds: [ContactCategory.MEMBER, ContactCategory.GUEST],
        ...reqModal
      }, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // LOGIN HISTORY

  searchLoginHistory = async (skip: number, take: number, reqModel: {
    memberId?: number,
    isLogin?: boolean,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }, forceRefresh?: boolean) => {
    this.loading = true;
    try {
      const cacheKey = JSON.stringify(reqModel);
      if (this.searchStaffLoginLogCache.has(cacheKey) && !forceRefresh) {
        return Promise.resolve(this.searchStaffLoginLogCache.get(cacheKey));
      }

      const res = await Axios.post(`/api/Member/Login/Log/Search/${skip}/${take}`, reqModel);
      this.searchStaffLoginLogCache.set(cacheKey, res.data);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // CHECK EDIT LOG
  searchLogHistory = async (skip: number, take: number, reqModel: {
    memberId?: number,
    membershipStatusId?: MembershipStatus | null,
    oriSystemId?: string | null,
    newSystemId?: string | null,
    logString?: string | null,
    changeCategoryId?: ChangeSectionCategory | null,
    approvalStatusId?: StatusCodes | null,
    approvedBy?: number | null,
    approvedAtFrom?: string | null,
    approvedAtTo?: string | null,
    createdBy?: number | null,
    changeFieldName?: string | null,
    createdAtFrom?: string | null,
    createdAtTo?: string | null,
    lastModifiedBy?: number | null,
    lastModifiedAtFrom?: string | null,
    lastModifiedAtTo?: string | null;
    includeSpecialLogs?: boolean;
  }, abortSignal?: AbortSignal) => {
    this.loadingChangeHistory = true;
    try {
      const res = await Axios.post<VmListT<ChangeLogReturnObject>>(`/api/Member/Change/Log/Search/${skip}/${take}`, reqModel, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingChangeHistory = false;
    }
  };




  // ============ PHOTO ID ============

  searchPhotoIdDocuments = async (req: {
    memberId?: number,
    categoryId?: number,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }) => {
    this.loadingPhotoIdDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/PhotoId/Search/0/0`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingPhotoIdDocuments = false;
    }
  };

  createPhotoIdDocument = async (photoIdDoc: {
    memberId: number,
    categoryId: number,
    startAt?: string,
    expiredAt?: string;
  }) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/PhotoId`, photoIdDoc);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };

  updatePhotoIdDocument = async (photoIdDoc: any, abortSignal?: AbortSignal) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.put(`/api/Member/PhotoId`, photoIdDoc, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };

  deletePhotoIdDocument = async (id: number, abortSignal?: AbortSignal) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.delete(`/api/Member/PhotoId/${id}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };

  // PHOTO ID ATTACHEMNT

  searchPhotoIdDocumentAttachmentList = async (reqModel: {
    documentId?: number,
    attachmentCategoryId?: AttachmentType,
    searchTerm?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/PhotoId/Attachment/Search/0/0/`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createPhotoIdDocumentAttachment = async (reqModel: {
    fileName: string,
    file: string,
    photoId: number;
  }) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.post(`/api/Member/PhotoId/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.PHOTO_ID,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };

  updatePhotoIdDocumentAttachment = async (reqModel: {
    memberId: number,
    categoryId: number,
    startAt: string,
    expiredAt: string,
    id: number;
  }) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.put(`/api/Member/PhotoId/Attachment`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };


  deletePhotoIdDocumentAttachment = async (id: number) => {
    this.savingPhotoIdDocuments = true;
    try {
      const res = await Axios.delete(`/api/Member/PhotoId/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingPhotoIdDocuments = false;
    }
  };

  changePassword = async (reqModel: {
    oldPassword: string,
    newPassword: string,
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Auth/Admin/Password/Change`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ========= CHANGE LOG ============

  searchChangeLog = async (skip: number = 0, take: number = 0, reqModel: {
    cMemberId?: number,
    memberId?: number,
    oriSystemId?: number,
    membershipStatusId?: number,
    changeCategoryId?: number,
    approvalStatusId?: number,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    changeFieldName?: string | null | undefined,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,

    sortBy?: ChangeLogSortBy | null,
    isAscending?: boolean | null,
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Change/Log/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ------- BATCH APPROVE -------

  approveMemberSection = async (reqModel: {
    memberId: number,
    changeCategoryIds: number[],
    note: string,
    approvalStatusId: number;
  }) => {
    this.approvingMemberSection = true;
    try {
      const res = await Axios.post(`/api/Member/Change/Log/Approved`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.approvingMemberSection = false;
    }
  };

  // CPD CREDIT

  searchCpdTotalCredit = async (skip: number, take: number, reqModel: {
    memberId?: number,
    memberStatusId?: number,
    isQualified?: boolean,
    year?: number,
    totalCpdCreditFrom?: number,
    totalCpdCreditTo?: number,
    totalCmasaCreditFrom?: number,
    totalCmasaCreditTo?: number,
    totalExternalCreditFrom?: number,
    totalExternalCreditTo?: number,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string,
    sortBy?: "MemberId" | "Name" | "Year" | "IsQualified" | "TotalCpdCredit" | "TotalCmasaCredit" | "TotalExternalCredit" | "CreatedAt" | "LastModifiedAt" | undefined,
    isAscending?: boolean | null,
  }, signal?: AbortSignal) => {
    this.loadingCpdTotalCredit = true;
    try {
      const res = await Axios.post(`/api/Cpd/Credit/Search/${skip}/${take}`, reqModel, { signal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingCpdTotalCredit = false;
    }
  };

  checkUsernameAvailability = async ({ username, contactCategoryId }: { username: string, contactCategoryId: ContactCategory; }, abortSignal?: AbortSignal) => {
    this.checkingUsernameAvailability = true;
    try {
      const res = await Axios.get(`/api/Auth/Check/Username?categoryId=${contactCategoryId}&username=${username}`, { signal: abortSignal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.checkingUsernameAvailability = false;
    }
  };

  searchAdminRoles = async (skip: number, take: number, reqModel: {
    contactId?: number,
    roleId?: number,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }, signal?: AbortSignal) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/AdminRole/Contact/Search/${skip}/${take}`, reqModel, { signal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  assignAdminRole = async (reqModel: {
    contactId: number,
    roleId: number;
  }) => {
    this.assigningAdminRole = true;
    try {
      const res = await Axios.post(`/api/AdminRole/Assign`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.assigningAdminRole = false;
    }
  };

  updateAdminContact = async (id: number, reqModel: {
    roleId: number;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.put(`/api/AdminRole/Contact/${id}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ============ REPORTING ============

  exportHealthFundReports = async (reqModel: {
    templateId: number;
    fileTypeId: ExportFileFormat;
  }, startFromDate: string, endToDate: string, closeByCPDYear?: number, signal?: AbortSignal) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/Report/HFs/Generate/${reqModel.templateId}/${reqModel.fileTypeId}?DateFrom=${startFromDate}&DateTo=${endToDate}${closeByCPDYear ? `&CloseByCpdYear=${closeByCPDYear}` : ''}`, {}, { responseType: 'arraybuffer', signal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  exportEntireTable = async (reqModel: {
    cpdYears: number[];
    includeOpenClinics?: boolean;
    includeClosedClinics?: boolean;
  }) => {
    this.downloadingEntireTable = true;
    try {
      const res = await Axios.post(`/api/Report/ALL/Generate`, reqModel, { responseType: 'arraybuffer' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingEntireTable = false;
    }
  };

  generateHealthFundReports = async (startFromDate: string, endToDate: string, closeByCPDYear?: number, signal?: AbortSignal) => {
    this.downloading = true;
    try {
      const res = await Axios.get(`/api/Report/Check?DateFrom=${startFromDate}&DateTo=${endToDate}${closeByCPDYear ? `&CloseByCpdYear=${closeByCPDYear}` : ''}`, { signal });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  // ============ EXPORT TEMPLATES ============

  getExportTemplate = async (templateId: number) => {
    this.loadingExportTemplate = true;
    try {
      const res = await Axios.get(`/api/Report/Template/${templateId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingExportTemplate = false;
    }
  };

  searchExportTemplates = async (skip: number, take: number, reqModel: {
    hfsId?: number,
    categoryId?: number,
    columnNameList?: string,
    filedList?: string,
    name?: string;
  }) => {
    this.loadingExportTemplate = true;
    try {
      const res = await Axios.post(`/api/Report/Template/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingExportTemplate = false;
    }
  };

  createExportTemplate = async (reqModel: ExportTemplate) => {
    this.savingExportTemplate = true;
    try {
      const res = await Axios.post(`/api/Report/Template`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingExportTemplate = false;
    }
  };

  updateExportTemplate = async (reqModel: ExportTemplate) => {
    this.savingExportTemplate = true;
    try {
      const res = await Axios.put(`/api/Report/Template`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingExportTemplate = false;
    }
  };

  deleteExportTemplate = async (id: number) => {
    this.loadingExportTemplate = true;
    try {
      const res = await Axios.delete(`/api/Report/Template/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingExportTemplate = false;
    }
  };

  // ============ EXPORTABLE KEYS ============

  searchExportableKeys = async () => {
    this.loadingExportableData = true;
    try {
      const res = await Axios.post(`/api/Report/HFs/Field/Search/0/0`, {});
      SetLocalStorage(EXPORTABLE_DATA, res.data.items);
      SetLocalStorage(EXPORTABLE_DATA_EXPIRY, Date.now() + 1000 * 60 * 60 * 1);  // 1 hour
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingExportableData = false;
    }
  };


  // ============ HISTORICAL REPORTS ============

  searchHistoricalReports = async (skip: number, take: number, reqModel: {
    hfsId?: number,
    year?: number,
    month?: number,
    attachmentCategoryId?: number,
    searchTerm?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Report/Attachment/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  downloadHistoricalReport = async (name: string) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Report/Attachment/${name}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  searchInsuranceProviderHistory = async (skip: number, take: number, reqModel: {
    memberId: number,
    clinicId: number,
    providerCategoryId?: number,
    acu?: boolean,
    rem?: boolean,
    chm?: boolean,
    // acuNo?: string,
    // remNo?: string,
    // chmNo?: string,
    // approvalStatusId?: number,
    // isActived?: boolean,
    approvedBy?: number,
    approvedAtFrom?: string,
    approvedAtTo?: string,
    createdBy?: number,
    createdAtFrom?: string,
    createdAtTo?: string,
    lastModifiedBy?: number,
    lastModifiedAtFrom?: string,
    lastModifiedAtTo?: string;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ============ IMPORT MEMBER LIST ============

  uploadInsuranceList = async (reqModel: {
    fileName: string,
    file: string;
  }) => {
    this.uploadingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/Import/Insurance`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.HEALTH_FUND_IMPORT,
      });
      return Promise.resolve(res.data as ImportMemberResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingInsuranceList = false;
    }
  };

  uploadMedibankInsurancePDFZIP_V2 = async (reqModal: {
    file: string,
    fileName: string,
  }) => {
    this.uploadingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/InsurancePdf/Upload`, {
        ...reqModal,
        attachmentCategoryId: AttachmentType.INSURANCE_DOCUMENT_MEDIBANK_ZIP,
      });
      return Promise.resolve(res.data as ImportMedibankFileResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingInsuranceList = false;
    }
  };

  uploadMedibankInsurancePDFZIP = async (reqModal: {
    file: string,
    fileName: string,
  }) => {
    this.uploadingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/InsurancePdf/MediNewMember`, {
        ...reqModal,
        attachmentCategoryId: AttachmentType.INSURANCE_DOCUMENT_MEDIBANK_ZIP,
      });
      return Promise.resolve(res.data as ImportMedibankFileResponse);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingInsuranceList = false;
    }
  };



  generateMedibankInsurancePDF = async (groupId: number) => {
    this.uploadingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/InsurancePdf/Generate/${groupId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingInsuranceList = false;
    }
  };

  sendMedibankInsurancePDF = async (groupId: number) => {
    this.uploadingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/InsurancePdf/Send/${groupId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploadingInsuranceList = false;
    }
  };

  searchMedibankInsurancePDF = async (skip: number, take: number, req: any) => {
    this.searchingInsuranceList = true;
    try {
      const res = await Axios.post(`/api/InsurancePdf/Search/${skip}/${take}`, req);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.searchingInsuranceList = false;
    }
  };

  // ============ MEMBERSHIP CERTIFICATE ============

  downloadMembershipCertificate = async (memberId: number) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/Contact/Membership/Certification/${memberId}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  getDefaultMembershipCertificateStringByMemberId = async (memberId: number) => {
    this.loading = true;
    try {
      const res = await Axios.post<string>(`/api/Contact/Membership/Certification/Default/String/${memberId}`, {});
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  // ============ CUSTOM MEMBERSHIP CERTIFICATE ============

  getCustomMembershipCertificateById = async (id: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Certificate/Custom/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getCustomMembershipCertificateByMemberId = async (memberId: number) => {
    this.loading = true;
    try {
      const res = await Axios.get(`/api/Member/Certificate/Custom/Member/${memberId}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createCustomMembershipCertificate = async (reqModel: {
    memberId: number,
    appendMembershipDateRange: boolean,
    customizedContent?: string | null;
  }) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.post(`/api/Member/Certificate/Custom`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  updateCustomMembershipCertificate = async (reqModel: {
    id: number,
    memberId: number,
    appendMembershipDateRange: boolean,
    customizedContent?: string | null;
  }) => {
    this.savingAccountDetails = true;
    try {
      const res = await Axios.put(`/api/Member/Certificate/Custom`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingAccountDetails = false;
    }
  };

  deleteCustomMembershipCertificate = async (id: number) => {
    this.deletingMember = true;
    try {
      const res = await Axios.delete(`/api/Member/Certificate/Custom/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingMember = false;
    }
  };

  searchCustomMembershipCertificates = async (skip: number, take: number, reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Member/Certificate/Custom/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  downloadCustomMembershipCertificate = async (memberId: number, isPreview: boolean = false) => {
    this.downloading = true;
    try {
      const res = await Axios.post(`/api/Member/Certificate/Custom/Generate/${memberId}?isPreview=${isPreview}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  // ============ INTERNAL NOTES ============

  searchInternalNotes = async (skip: number, take: number, reqModel: {
    memberId: number,
    searchTerm?: string;
  }) => {
    this.loadingInternalNotes = true;
    try {
      const res = await Axios.post(`/api/Member/Note/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInternalNotes = false;
    }
  };

  createInternalNote = async (reqModel: {
    memberId: number,
    note: string;
  }) => {
    this.savingInternalNote = true;
    try {
      const res = await Axios.post(`/api/Member/Note`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInternalNote = false;
    }
  };

  updateInternalNote = async (reqModel: {
    id: number,
    note: string,
    memberId: number;
  }) => {
    this.savingInternalNote = true;
    try {
      const res = await Axios.put(`/api/Member/Note`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInternalNote = false;
    }
  };

  deleteInternalNote = async (id: number) => {
    this.deletingInternalNote = true;
    try {
      const res = await Axios.delete(`/api/Member/Note/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deletingInternalNote = false;
    }
  };

  // ============ CLOSE MEMBER CLINICS ============

  closeMemberClinics = async (reqModal: {
    dateFrom: string,
    dateTo: string;
    closeByCpdYear?: number;
  }) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Report/Close`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  getLatestExportRange = async () => {
    this.loading = true;
    try {
      const res = await Axios.get<VmListT<ReportExportLog>>(`/api/Report/Search/Close`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  checkZeroCpdPoints = async (cpdYear: number, dateFrom: string) => {
    this.loadingZeroCpdMembers = true;
    try {
      const res = await Axios.get(`/api/Report/Check/ZeroCpdPoints?cpdYear=${cpdYear}&DateFrom=${dateFrom}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingZeroCpdMembers = false;
    }
  };

  downloadCloseMemberList = async (attachmentName: string) => {
    this.downloading = true;
    try {
      const res = await Axios.get(`/api/Report/Close/Attachment/${attachmentName}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  // ============ CLINIC INFORMATION EXPORT ============

  downloadClinicInformation = async (memberId: number) => {
    this.generatingClinicPDF = true;
    try {
      const res = await Axios.post(`/api/Contact/Membership/Detail/${memberId}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.generatingClinicPDF = false;
    }
  };

  // ============ RESUBMIT MEMBERSHIP APPLICATION ============

  toggleRejectOrPendingMember = async (memberId: number, isRejected: boolean) => {
    this.loading = true;
    try {
      const api = isRejected ? `/api/Payment/Reject/Member?memberId=${memberId}` : `/api/Contact/Resubmit/${memberId}`;
      const res = await Axios.put(api, {});
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  // ============ DEACTIVATE MEMBERSHIP ============

  deactivateMembership = async (reqModal: {
    memberId: number,
    isTerminate: boolean,
    isLoginAllowed: boolean,
    shouldSendNotification: boolean,
    terminateReason: string,
  }) => {
    this.deactivatingMembership = true;
    try {
      const res = await Axios.post(`/api/Contact/Terminate`, reqModal);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deactivatingMembership = false;
    }
  };

  changeMembershipDeactivationConfig = async (reqModel: {
    memberId: number,
    isLoginAllowed: boolean,
    shouldSendNotification: boolean;
  }) => {
    this.deactivatingMembership = true;
    try {
      const res = await Axios.post(`/api/Contact/Access`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deactivatingMembership = false;
    }
  };

  // =================== NOTIFICATION CONTACT SEARCH ===================
  searchNotificationContacts = async (skip: number, take: number, reqModel: searchNotificationContactsBody, abortSignal?: AbortSignal, searchConfig?: { useCache: boolean; }) => {
    this.loadingNotificationContacts = true;
    try {
      const cacheKey = `${JSON.stringify(reqModel)}_${skip}_${take}`;
      if (searchConfig?.useCache !== false && this.searchNotificationContactsCache.has(cacheKey)) {
        const cacheInfo = this.searchNotificationContactsCache.get(cacheKey);
        if (cacheInfo.expiry > Date.now()) {
          return Promise.resolve(cacheInfo.data);
        }
      }

      const res = await Axios.post<NotificationContactSearchResponse>(`/api/Notification/Search/Contact/${skip}/${take}`, {
        contactCategoryIds: [ContactCategory.MEMBER, ContactCategory.GUEST],
        ...reqModel,
      }, { signal: abortSignal });
      this.searchNotificationContactsCache.set(cacheKey, {
        data: res.data,
        expiry: Date.now() + 1000 * 60 * 2,  // 2 mins
      });
      res.data.items.map((c: SimplifiedAccountDetails) => this.notificationContactInfoCache.set(c.id, c));
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingNotificationContacts = false;
    }
  };

  // ============ INSURANCE PROVIDER ATTACHMENT ============

  searchInsuranceDocument = async (skip: number, take: number, reqModel: {
    memberIds?: number[],
    hfCategoryId?: number,
    attachmentCategoryId?: number,
    searchTerm?: string;
  }) => {
    this.loadingInsuranceDocument = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Document/Attachment/Search/${skip}/${take}`, {
        ...reqModel,
        hfCategoryId: reqModel.hfCategoryId === GENERAL_DOCUMENT_LOCAL_CATEGORY_ID ? 0 : reqModel.hfCategoryId,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInsuranceDocument = false;
    }
  };

  getInsuranceDocument = async (id: number) => {
    this.loadingInsuranceDocument = true;
    try {
      const res = await Axios.get(`/api/Member/Insurance/Document/Attachment/Info/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInsuranceDocument = false;
    }
  };

  createInsuranceDocument = async (reqModel: {
    fileName: string,
    file: string, // BASE64
    clinicOriIdList?: number[] | null,
    memberId: number,
    hfCategoryId?: number | null
  }) => {
    this.savingInsuranceDocument = true;
    try {
      const res = await Axios.post(`/api/Member/Insurance/Document/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.INSURANCE_DOCUMENT,
        hfCategoryId: reqModel.hfCategoryId === GENERAL_DOCUMENT_LOCAL_CATEGORY_ID ? null : reqModel.hfCategoryId,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInsuranceDocument = false;
    }
  };

  updateInsuranceDocument = async (reqModel: {
    memberId: number,
    hfCategoryId?: number | null,
    originalName: string,
    id: number,
    clinicOriIdList?: number[] | null,
  }) => {
    this.savingInsuranceDocument = true;
    try {
      const res = await Axios.put(`/api/Member/Insurance/Document/Attachment`, {
        ...reqModel,
        attachmentCategoryId: AttachmentType.INSURANCE_DOCUMENT,
        hfCategoryId: reqModel.hfCategoryId === GENERAL_DOCUMENT_LOCAL_CATEGORY_ID ? null : reqModel.hfCategoryId,
      });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInsuranceDocument = false;
    }
  };

  downloadInsuranceDocument = async (name: string) => {
    this.downloadingInsuranceDocument = true;
    try {
      const res = await Axios.get(`/api/Member/Insurance/Document/Attachment/${name}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingInsuranceDocument = false;
    }
  };

  deleteInsuranceDocument = async (id: number) => {
    this.savingInsuranceDocument = true;
    try {
      const res = await Axios.delete(`/api/Member/Insurance/Document/Attachment/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.savingInsuranceDocument = false;
    }
  };


  // ============= RESET PASSWORD =============

  sendResetPasswordEmail = async (username: string) => {
    this.sendingResetPasswordEmail = true;
    try {
      const res = await Axios.get(`/api/Auth/Member/Password/Forget?username=${username}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.sendingResetPasswordEmail = false;
    }
  };


  // ============= DOWNLOAD MEMBERSHIP APPLICATION FORM =============

  downloadMembershipApplicationForm = async (memberId: number) => {
    this.downloading = true;
    try {
      if (isDevelopment) {
        this.rootStore.notify("PENDING IMPLEMENTATION - Download Membership Application Form", 'warning');
        return Promise.resolve(new Blob());
      }
      const res = await Axios.get(`/api/Member/Application/Download/${memberId}`, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  };

  createSignature = async (reqModel: any) => {
    this.signatureLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Signature`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.signatureLoading = false;
    }
  };

  deleteSignature = async (id: number) => {
    this.signatureLoading = true;
    try {
      const res = await Axios.delete(`/api/Member/Signature/${id}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.signatureLoading = false;
    }
  };

  searchSignature = async (skip: number, take: number, reqModel: any) => {
    this.signatureLoading = true;
    try {
      const res = await Axios.post(`/api/Member/Signature/Search/${skip}/${take}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.signatureLoading = false;
    }
  };



  generateMemberApplicationFormPDF = async (memberId: number) => {
    this.downloadingApplicationForm = true;
    try {
      const res = await Axios.post(`/api/Contact/ApplicationForm/Generate/${memberId}`, {}, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloadingApplicationForm = false;
    }
  };
}

export enum ChangeLogSortBy {
  MEMBER_EXPIRY = 1,
  UPDATED_AT = 2,
}

